/**
 * One tile of the video wall = one (device, stream) pair, played over WHEP.
 *
 * ★ What this component is NOT allowed to do, because the prototype did all of it:
 *   · show a placeholder image and call it a camera;
 *   · show "28ms / 30fps / direct" as constants (every number here comes from
 *     useWhep's getStats sampling, and a stat we could not read renders as "—");
 *   · show a "frame frozen" warning from a mock field (freeze is now derived from
 *     framesDecoded standing still while the connection is up);
 *   · show a "direct / relayed" badge — for the console, media ALWAYS goes through
 *     the relay on panda02, so the honest label is a fixed one, not a guess.
 *
 * ★ The tile opens its peer connection only while mounted AND enabled. That is the
 *   whole lazy-pull contract: nobody watching => the relay pulls nothing => the
 *   dog's cellular uplink stays at zero (design-hub-video-fanout-20260801.md §5.1).
 */
import { useEffect, useRef, useState } from 'react';
import { useWhep, type WhepState, type WhepStats } from '@/hooks/useWhep';
import { typeIcons } from '@/lib/terminalLabels';
import type { HubVideoStream } from '@/lib/hubClient';

export interface TileSpec {
  deviceId: string;
  deviceName: string;
  deviceType: string;
  /** true = the registry/private net says it is online. Independent of video. */
  deviceOnline: boolean;
  /** Streams the relay has configured for this device. */
  streams: HubVideoStream[];
}

/** Status chip text + colour. Each state says what to DO, not just that it is bad. */
const STATE_LABEL: Record<WhepState, { text: string; dot: string; hint?: string }> = {
  idle: { text: '未连接', dot: 'bg-foreground-400', hint: '这一格没有在拉流 —— 设备上行为 0' },
  connecting: { text: '连接中', dot: 'bg-amber-400 animate-pulse' },
  playing: { text: '在播', dot: 'bg-accent-500' },
  frozen: { text: '画面冻结', dot: 'bg-amber-500', hint: '连接还在, 但已经数不到新帧 —— 屏幕上这一帧是旧的' },
  nosignal: { text: '无信号', dot: 'bg-red-500', hint: '中继答复了, 但取不到画面:设备侧 go2rtc 或私有网这一段' },
  unconfigured: { text: '中继未部署', dot: 'bg-foreground-400', hint: 'hub 没配 OXMAN_HUB_VIDEO_RELAY' },
  unreachable: { text: '中继不可达', dot: 'bg-red-500', hint: '信令这一跳就断了 —— 查 panda02 上的 oxman-video-relay' },
  denied: { text: '无权观看', dot: 'bg-red-500', hint: '令牌过期或被吊销 —— 重新登录' },
};

/** null renders as an em dash, never as 0 (§9b). */
function num(v: number | null, digits = 0, unit = ''): string {
  if (v === null || !isFinite(v)) return '—';
  return `${v.toFixed(digits)}${unit}`;
}

function StatRow({ stats, state }: { stats: WhepStats; state: WhepState }) {
  // Only show measurements while a session is actually up. Keeping last-known
  // numbers on a dead tile is the same lie as a frozen picture.
  const live = state === 'playing' || state === 'frozen';
  const res = live && stats.width && stats.height ? `${stats.width}×${stats.height}` : '—';
  return (
    <div className="flex items-center gap-3 flex-wrap px-2 py-1.5 text-[10px] font-mono text-white/70 bg-black/45">
      <span title="解码帧率(实测)">{live ? num(stats.fps, 1, ' fps') : '—'}</span>
      <span title="入站视频码率(实测)">{live ? num(stats.kbps, 0, ' kbps') : '—'}</span>
      <span title="分辨率(实测)">{res}</span>
      <span title="ICE 往返时延(实测)">{live ? num(stats.rttMs, 0, ' ms') : '—'}</span>
      <span title="丢包率(分母 = 收到+丢失)">
        {live && stats.loss !== null ? `丢 ${(stats.loss * 100).toFixed(1)}%` : '丢 —'}
      </span>
    </div>
  );
}

export default function StreamCard({ tile, enabled }: { tile: TileSpec; enabled: boolean }) {
  // Which stream this tile shows. Defaults to the device's first configured one;
  // the picker below switches it (and tears the old peer connection down).
  const [stream, setStream] = useState(tile.streams[0]?.stream ?? '');
  const { state, detail, stats, attachVideo, retry } = useWhep(tile.deviceId, stream, enabled && !!stream);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const label = STATE_LABEL[state];

  // If the relay's stream list changes under us (camera added / removed), keep
  // pointing at something that exists instead of holding a dead name.
  useEffect(() => {
    if (stream && tile.streams.some(s => s.stream === stream)) return;
    setStream(tile.streams[0]?.stream ?? '');
  }, [tile.streams, stream]);

  const current = tile.streams.find(s => s.stream === stream);
  const showVideo = state === 'playing' || state === 'frozen';

  return (
    <div ref={boxRef} className="relative bg-foreground-950 rounded-lg overflow-hidden select-none">
      <div className="relative w-full aspect-video bg-foreground-900">
        <div className="absolute top-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-b from-black/70 to-transparent z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-3 h-3 flex items-center justify-center shrink-0">
                <i className={`${typeIcons[tile.deviceType] ?? typeIcons.other} text-[10px] text-white`} />
              </span>
              <span className="text-xs text-white font-medium truncate">{tile.deviceName}</span>
              {/* Two independent facts, deliberately shown separately: the device
                  is registered/online, and this video session's state. A device can
                  be online while its camera produces nothing. */}
              <span className="text-[10px] text-white/40 shrink-0">{tile.deviceOnline ? '在线' : '离线'}</span>
            </div>
            <span title={label.hint ?? ''}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-black/50 text-white/85 shrink-0">
              <span className={`w-1.5 h-1.5 rounded-full ${label.dot}`} />
              {label.text}
            </span>
          </div>
        </div>

        {/* muted + playsInline: autoplay policy blocks anything else, and a wall of
            tiles must never fight over audio. */}
        <video
          ref={attachVideo}
          muted
          playsInline
          autoPlay
          className={`w-full h-full object-contain bg-black ${showVideo ? '' : 'hidden'}`}
        />

        {!showVideo && (
          <div className="flex flex-col items-center justify-center h-full text-white/40 px-4 text-center">
            <span className="w-10 h-10 flex items-center justify-center mb-2">
              <i className={`${state === 'connecting' ? 'ri-loader-4-line animate-spin' : 'ri-video-off-line'} text-2xl`} />
            </span>
            <p className="text-sm text-white/70">{label.text}</p>
            {(detail || label.hint) && (
              <p className="text-[11px] mt-1 text-white/35 leading-relaxed max-w-[22rem] break-words">
                {detail || label.hint}
              </p>
            )}
            {(state === 'denied' || state === 'unconfigured' || state === 'nosignal' || state === 'unreachable') && (
              <button onClick={retry}
                className="mt-3 px-2.5 py-1 text-[11px] rounded-md bg-white/10 text-white/80 hover:bg-white/20 cursor-pointer whitespace-nowrap transition-all">
                重试
              </button>
            )}
          </div>
        )}

        {state === 'frozen' && (
          // A still picture is the most convincing lie a video wall can tell, so
          // say it on top of the picture, not only in the corner chip.
          <div className="absolute inset-x-0 bottom-8 flex justify-center pointer-events-none">
            <span className="px-2 py-1 rounded bg-amber-500/90 text-[11px] text-black font-medium">
              画面已冻结 —— 这一帧是旧的
            </span>
          </div>
        )}
      </div>

      <StatRow stats={stats} state={state} />

      {tile.streams.length > 0 && (
        <div className="flex items-center gap-1 px-2 py-1.5 bg-black/60 flex-wrap">
          {tile.streams.map(s => (
            <button key={s.stream} onClick={() => setStream(s.stream)}
              title={`中继流名 ${s.relayName};此刻 ${s.consumers} 个观看者, 上游 ${s.pulling}/${s.sources} 在拉`}
              className={`px-2 py-0.5 rounded text-[10px] font-mono cursor-pointer whitespace-nowrap transition-all ${
                s.stream === stream ? 'bg-primary-500 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>
              {s.stream}
            </button>
          ))}
          {/* These two numbers ARE the fan-out proof, which is why they are on the
              tile and not in a debug panel: N viewers on one stream must still show
              ONE upstream pull. `pulling` is deliberately not `sources` — the relay
              reports a configured-but-idle upstream too, and counting those said
              "upstream 1" while nothing was connected (measured 2026-08-08). */}
          {current && (
            <span className="ml-auto text-[10px] text-white/40 font-mono"
              title={`观看者 ${current.consumers} 人;上游 ${current.pulling}/${current.sources} 在拉。` +
                '不管几个人看, 上游最多 1 条 —— 那就是扇出生效的证据。上游 0 = 此刻没人看, 设备上行为 0。'}>
              观看 {current.consumers} · 上游 {current.pulling}/{current.sources}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
